/**
 * Surface Base Component and Interface
 * 
 * This file provides standardized interfaces and base components for all surface components
 * to ensure consistency across the FreshCards application.
 */

import React, { useState, useEffect } from 'react';
import { useMantineTheme } from '@mantine/core';

// Common surface props interface
export interface SurfaceProps {
  /** Surface identifier */
  id: string;
  /** Current phase number */
  phase: number;
  /** Surface title */
  title: string;
  /** Surface description */
  description?: string;
  /** Whether the surface is currently active */
  isActive?: boolean;
  /** Whether the surface is loading */
  isLoading?: boolean;
  /** Error state */
  error?: string;
  /** Children content */
  children?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Callback when surface is closed */
  onClose?: () => void;
  /** Callback when surface state changes */
  onStateChange?: (state: SurfaceState) => void;
}

// Surface state interface
export interface SurfaceState {
  /** Whether the surface is active */
  isActive: boolean;
  /** Whether the surface is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Surface metadata */
  metadata: {
    /** Surface type */
    type: string;
    /** Last updated timestamp */
    lastUpdated: number;
    /** Version number */
    version: string;
  };
}

// Default surface state
export const defaultSurfaceState: SurfaceState = {
  isActive: false,
  isLoading: false,
  error: null,
  metadata: {
    type: 'default',
    lastUpdated: Date.now(),
    version: '1.0.0'
  }
};

// Surface header component
export const SurfaceHeader: React.FC<{
  title: string;
  description?: string;
  phase: number;
  onClose?: () => void;
  className?: string;
}> = ({ title, description, phase, onClose, className = '' }) => {
  const theme = useMantineTheme();

  return (
    <div className={`surface-header ${className}`}>
      <div className="surface-header-content">
        <div className="surface-title-section">
          <h2 className="surface-title">{title}</h2>
          {description && (
            <p className="surface-description">{description}</p>
          )}
        </div>
        <div className="surface-header-actions">
          <div className="surface-phase-badge">
            <span className="phase-number">Phase {phase}</span>
          </div>
          {onClose && (
            <button
              className="surface-close-btn"
              onClick={onClose}
              title="Close surface"
              aria-label="Close surface"
            >
              ✕
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Surface loading component
export const SurfaceLoading: React.FC<{
  message?: string;
  className?: string;
}> = ({ message = 'Loading...', className = '' }) => {
  const theme = useMantineTheme();

  return (
    <div className={`surface-loading ${className}`}>
      <div className="loading-spinner">
        <div className="spinner-ring"></div>
      </div>
      <p className="loading-message">{message}</p>
    </div>
  );
};

// Surface error component
export const SurfaceError: React.FC<{
  error: string;
  onRetry?: () => void;
  className?: string;
}> = ({ error, onRetry, className = '' }) => {
  const theme = useMantineTheme();

  return (
    <div className={`surface-error ${className}`}>
      <div className="error-icon">⚠️</div>
      <div className="error-message">
        <h3>Error</h3>
        <p>{error}</p>
      </div>
      {onRetry &&(
        <button
          className="error-retry-btn"
          onClick={onRetry}
        >
          Retry
        </button>
      )}
    </div>
  );
};

// Surface container component
export const SurfaceContainer: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => {
  return (
    <div className={`surface-container ${className}`}>
      {children}
    </div>
  );
};

// Base surface component that all surfaces should extend
export const BaseSurface: React.FC<SurfaceProps> = ({
  id,
  phase,
  title,
  description,
  isActive = true,
  isLoading = false,
  error,
  children,
  className = '',
  onClose,
  onStateChange
}) => {
  const [state, setState] = useState<SurfaceState>({
    ...defaultSurfaceState,
    isActive,
    isLoading,
    error: error || null
  });

  const theme = useMantineTheme();

  // Update state when props change
  useEffect(() => {
    setState(prev => ({
      ...prev,
      isActive,
      isLoading,
      error: error || null,
      metadata: {
        ...prev.metadata,
        lastUpdated: Date.now()
      }
    }));
  }, [isActive, isLoading, error]);

  // Notify parent of state changes
  useEffect(() => {
    onStateChange?.(state);
  }, [state, onStateChange]);

  // Handle close
  const handleClose = () => {
    setState(prev => ({ ...prev, isActive: false }));
    onClose?.();
  };

  return (
    <SurfaceContainer className={`surface ${id} ${className}`}>
      <SurfaceHeader
        title={title}
        description={description || undefined}
        phase={phase}
        onClose={onClose ? handleClose : undefined}
      />
      
      <div className="surface-content">
        {isLoading && <SurfaceLoading />}
        {error && <SurfaceError error={error} />}
        {!isLoading && !error && children}
      </div>
    </SurfaceContainer>
  );
};